/**
 * APSelectMenu Component
 *
 * The dropdown menu component for the APSelect.
 * Handles option display, search functionality, and menu interactions.
 */

import React, { useEffect, useRef, useState } from 'react';

import { useTranslate } from '@shared/hooks/use-translate';

import type { IAPSelectMenuProps } from './types';

import APSelectMenuItem from '../menu-item';

export const APSelectMenu: React.FC<IAPSelectMenuProps> = ({
  options,
  selectedValue,
  size,
  maxHeight = 200,
  searchable,
  searchPlaceholder = 'Search...',
  onSelect,
  onClose,
}) => {
  const translate = useTranslate();
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Filter options based on search term
  const filteredOptions = searchable
    ? options.filter((option) => String(option.label ?? '').toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="apds-select__menu"
      style={{ maxHeight }}
      role="listbox"
      aria-label={translate('aria.select-options')}
    >
      {searchable && (
        <input
          type="text"
          className="apds-select__search-input"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      )}

      <div>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <APSelectMenuItem
              key={option.value}
              option={option}
              isSelected={selectedValue === option.value}
              size={size}
              onClick={() => onSelect(option.value)}
            />
          ))
        ) : (
          <div className="apds-select__empty">
            {searchable && searchTerm ? 'No options found' : 'No options available'}
          </div>
        )}
      </div>
    </div>
  );
};

export default APSelectMenu;
